/** Must match `pickupReviewStatuses` in `./pickupReview.mocks` (kept local to avoid imports). */
const PICKUP_REVIEW_STATUSES = [
  "Pending",
  "Awaiting Customer",
  "Cancelled",
] as const;

/**
 * Warning messages for pickup review detail page
 */
export const PICKUP_WARNING_MESSAGES = {
  warnings: [
    {
      id: "warning-1",
      message: "Pickup close time is before 3:00 PM local time",
    },
    {
      id: "warning-2",
      message: "Pickup close time is before 3:00 PM local time",
    },
  ],
};

/**
 * Mock data for expanded freight party display (consignee/bill-to)
 */
export const mockExpandedFreightPartyDisplay = {
  consigneeName: "Midwest Distribution LLC. (200892)",
  consigneeStatusChip: "ACTIVE",
  billToName: "XYZ Distribution LLC. (200892)",
  billToStatusChip: "CREDIT APPROVED",
  addressStreet: "4820 E Broadway Blvd",
  addressCity: "Tucson",
  addressState: "AZ",
  addressZip: "85711",
  billToAddressStreet: "4820 E Broadway Blvd",
  billToAddressCity: "Tucson",
  billToAddressState: "AZ",
  billToAddressZip: "85711",
};

/**
 * Mock display fallbacks for pickup review detail page
 */
export const mockDisplayFallbacks = {
  accessorialList: [
    "Overlength 12 Ft But Less Than 20 Ft",
    "Inside Pickup Required",
    "Grocery Warehouse Delivery",
  ],
  pickupAccessorialFallback: ["Inside Pickup Required"],
  deliveryAccessorialFallback: ["Delivery Appointment Required"],
  driverNotes:
    "Dock #4 – Call 30 minutes before arrival. Forklift available on site.",
  bolNotes:
    "Freight shrink-wrapped and labeled by pallet. All pallets staged near shipping dock.",
  daylightAmount: "$5,000",
  traceNumber: "PO Number: 4578230041",
  huDescription: "Item",
  huNmfc: "19390",
  huSub: "Sub 1",
  huClass: 85,
  huWeight: 1000,
  huLength: 40,
  huWidth: 30,
  huHeight: 48,
  units: {
    weight: " lbs",
    length: '"',
    linearFeet: " ft",
    density: " lbs/ft³",
  },
};

/**
 * Generate mock pickup detail data for a specific pickup ID
 * This is used when viewing a pickup from the pickup review page
 * Returns data compatible with pickup entry form mock consumers
 */
export const generateMockPickupReviewDetailData = (pickupId: string) => {
  const index = parseInt(pickupId.replace(/\D/g, ""), 10) || 0;

  const shippers = [
    {
      name: "Acme Distribution Inc.",
      code: "AC3456",
      address: "1250 W Fulton Market",
      city: "Chicago",
      state: "IL",
      zip: "60607",
      phone: "+1(312)-555-0100",
      contact: "John Smith",
    },
    {
      name: "Midwest Logistics LLC",
      code: "MW2089",
      address: "4820 S Broadway Blvd",
      city: "Tempe",
      state: "AZ",
      zip: "85711",
      phone: "+1(480)-555-0200",
      contact: "Jane Doe",
    },
    {
      name: "Pacific Freight Services",
      code: "PF8821",
      address: "1500 Harbor Blvd",
      city: "Long Beach",
      state: "CA",
      zip: "90802",
      phone: "+1(562)-555-0300",
      contact: "Bob Johnson",
    },
  ];

  const consignees = [
    {
      name: "ABC Technologies Pvt. Ltd",
      code: "200892",
      address: "4820 S Broadway Blvd",
      city: "Tempe",
      state: "AZ",
      zip: "85711",
      phone: "+1(480)-555-9012",
      contact: "Daniel Brooks",
    },
    {
      name: "Global Supply Chain Inc.",
      code: "300456",
      address: "2500 Main St",
      city: "Dallas",
      state: "TX",
      zip: "75201",
      phone: "+1(214)-555-0400",
      contact: "Sarah Williams",
    },
    {
      name: "Eastern Distribution Center",
      code: "400789",
      address: "3000 Industrial Way",
      city: "Atlanta",
      state: "GA",
      zip: "30303",
      phone: "+1(404)-555-0500",
      contact: "Michael Chen",
    },
  ];

  const billTos = [
    {
      name: "ABC Technologies Pvt. Ltd",
      code: "200892",
      address: "4820 S Broadway Blvd",
      city: "Tempe",
      state: "AZ",
      zip: "85711",
      phone: "+1(480)-555-9012",
      contact: "Finance Dept",
    },
    {
      name: "Corporate Billing Services",
      code: "500123",
      address: "1000 Corporate Dr",
      city: "New York",
      state: "NY",
      zip: "10001",
      phone: "+1(212)-555-0600",
      contact: "Accounts Payable",
    },
  ];

  const shipper = shippers[index % shippers.length];
  const consignee = consignees[index % consignees.length];
  const billTo = billTos[index % billTos.length];

  const statuses = PICKUP_REVIEW_STATUSES;

  return {
    // Root level fields
    collectionType: "prepaid" as const,
    callerAccountSearch: shipper.code,
    shipperAccountSearch: shipper.code,
    shipperAccount: shipper.code,
    consigneeAccount: consignee.code,
    callerAccount: shipper.code,
    billToAccount: billTo.code,

    // Shipper details
    shipperAccountName: `${shipper.name} - #${shipper.code}`,
    shipperAddress: shipper.address,
    shipperCity: shipper.city,
    shipperState: shipper.state,
    shipperZip: shipper.zip,
    shipperContactName: shipper.contact,
    shipperContactPhone: shipper.phone,
    shipperCountry: "USA",

    // Caller details (same as shipper for this mock)
    callerAccountName: `${shipper.name} - #${shipper.code}`,
    callerAddress: shipper.address,
    callerCity: shipper.city,
    callerState: shipper.state,
    callerZip: shipper.zip,
    callerCountry: "USA",
    callerContactName: shipper.contact,
    callerContactPhone: shipper.phone,

    // Consignee details (root level, for first shipment)
    consigneeAccountName: `${consignee.name} - #${consignee.code}`,
    consigneeAddress: consignee.address,
    consigneeCity: consignee.city,
    consigneeState: consignee.state,
    consigneeZip: consignee.zip,
    consigneeCountry: "USA",
    consigneeContactName: consignee.contact,
    consigneeContactPhone: consignee.phone,

    // Bill To details (root level)
    billToAccountName: `${billTo.name} - #${billTo.code}`,
    billToAddress: billTo.address,
    billToCity: billTo.city,
    billToState: billTo.state,
    billToZip: billTo.zip,
    billToCountry: "USA",
    billToContactName: billTo.contact,
    billToContactPhone: billTo.phone,

    // Freight items (root level - legacy)
    freightItems: [
      {
        id: "1",
        pieces: "10",
        pieceType: "PCS - PIECES",
        description: "Item",
        nmfc: "19390",
        sub: "Sub 1",
        class: 85,
        weight: "1000",
        isPrimary: true,
      },
    ],

    // Handling units (root level - legacy)
    handlingUnits: [
      {
        id: "1",
        handlingUnit: "Pallets",
        quantity: "4",
        length: 40,
        width: 30,
        height: 48,
        doNotStack: false,
        isPrimary: true,
        items: [
          {
            id: "1-item-1",
            pieces: "10",
            pieceType: "PCS - PIECES",
            description: "Item",
            nmfc: "19390",
            sub: "Sub 1",
            class: 85,
            weight: "1000",
            isPrimary: true,
          },
        ],
      },
    ],

    linearFeet: "4",

    // Additional services (root level)
    additionalServices: {
      accessorial: [],
      pickup: ["Inside Pickup Required"],
      delivery: ["Delivery Appointment Required"],
    },

    shippingInstructions: {
      selectedServices: [
        {
          id: 1,
          title: "Overlength 12 Ft But Less Than 20 Ft",
        },
      ],
    },

    daylightProtect: {
      declareAmount: "$5,000",
      purchaseCoverage: true,
    },

    additionalInfo: {
      driverNotes:
        "Dock #4 – Call 30 minutes before arrival. Forklift available on site.",
      bolNotes:
        "Freight shrink-wrapped and labeled by pallet. All pallets staged near shipping dock.",
      additionalFields: [
        {
          id: "1",
          traceNumber: "PO Number: 4578230041",
          probill: "",
        },
      ],
    },

    // Shipments array (per-shipment data)
    shipments: [
      {
        collectionType: "prepaid" as const,
        consigneeAccount: consignee.code,
        consigneeAccountName: `${consignee.name} - #${consignee.code}`,
        consigneeAddress: consignee.address,
        consigneeCity: consignee.city,
        consigneeState: consignee.state,
        consigneeZip: consignee.zip,
        consigneeCountry: "USA",
        consigneeContactName: consignee.contact,
        consigneeContactPhone: consignee.phone,

        billToAccount: billTo.code,
        billToAccountName: `${billTo.name} - #${billTo.code}`,
        billToAddress: billTo.address,
        billToCity: billTo.city,
        billToState: billTo.state,
        billToZip: billTo.zip,
        billToCountry: "USA",
        billToContactName: billTo.contact,
        billToContactPhone: billTo.phone,

        handlingUnits: [
          {
            id: "1",
            handlingUnit: "Pallets",
            quantity: "4",
            length: 40,
            width: 30,
            height: 48,
            doNotStack: false,
            isPrimary: true,
            items: [
              {
                id: "1-item-1",
                pieces: "10",
                pieceType: "PCS - PIECES",
                description: "Item",
                nmfc: "19390",
                sub: "Sub 1",
                class: 85,
                weight: "1000",
                isPrimary: true,
              },
            ],
          },
        ],

        linearFeet: "4",
        serviceLevel: "LTL",
        specialShipmentTags: "",

        criticalUrgentCareAppointment: {
          deliveryDate: undefined,
          appointmentStartTime: undefined,
          appointmentEndTime: undefined,
          approvedBy: "",
        },

        customsBrokerName: "",

        freightForwarder: {
          company: "",
          phone: "",
          referenceNumber: "",
          contactName: "",
          email: "",
          address: "",
          website: "",
        },

        additionalServices: {
          accessorial: [],
          pickup: ["Inside Pickup Required"],
          delivery: ["Delivery Appointment Required"],
        },

        shippingInstructions: {
          selectedServices: [
            {
              id: 1,
              title: "Overlength 12 Ft But Less Than 20 Ft",
            },
          ],
        },

        daylightProtect: {
          declareAmount: "$5,000",
          purchaseCoverage: true,
        },

        additionalInfo: {
          driverNotes:
            "Dock #4 – Call 30 minutes before arrival. Forklift available on site.",
          bolNotes:
            "Freight shrink-wrapped and labeled by pallet. All pallets staged near shipping dock.",
          additionalFields: [
            {
              id: "1",
              traceNumber: "PO Number: 4578230041",
              probill: "",
            },
          ],
        },
      },
      {
        collectionType: "prepaid" as const,
        consigneeAccount: consignee.code,
        consigneeAccountName: `${consignee.name} - #${consignee.code}`,
        consigneeAddress: consignee.address,
        consigneeCity: consignee.city,
        consigneeState: consignee.state,
        consigneeZip: consignee.zip,
        consigneeCountry: "USA",
        consigneeContactName: consignee.contact,
        consigneeContactPhone: consignee.phone,

        billToAccount: billTo.code,
        billToAccountName: `${billTo.name} - #${billTo.code}`,
        billToAddress: billTo.address,
        billToCity: billTo.city,
        billToState: billTo.state,
        billToZip: billTo.zip,
        billToCountry: "USA",
        billToContactName: billTo.contact,
        billToContactPhone: billTo.phone,

        handlingUnits: [
          {
            id: "1",
            handlingUnit: "Pallets",
            quantity: "2",
            length: 48,
            width: 40,
            height: 50,
            doNotStack: false,
            isPrimary: true,
            items: [
              {
                id: "1-item-1",
                pieces: "20",
                pieceType: "CTN - CARTONS",
                description:
                  "Electronics Equipment and Computer Hardware Components",
                nmfc: "50400",
                sub: "Sub 2",
                class: 100,
                weight: "600",
                isPrimary: true,
              },
            ],
          },
        ],

        linearFeet: "4",
        serviceLevel: "LTL",
        specialShipmentTags: "",

        criticalUrgentCareAppointment: {
          deliveryDate: undefined,
          appointmentStartTime: undefined,
          appointmentEndTime: undefined,
          approvedBy: "",
        },

        customsBrokerName: "",

        freightForwarder: {
          company: "",
          phone: "",
          referenceNumber: "",
          contactName: "",
          email: "",
          address: "",
          website: "",
        },

        additionalServices: {
          accessorial: [],
          pickup: [],
          delivery: ["Residential Delivery"],
        },

        shippingInstructions: {
          selectedServices: [],
        },

        daylightProtect: {
          declareAmount: undefined,
          purchaseCoverage: false,
        },

        additionalInfo: {
          driverNotes: "Residential delivery - call ahead.",
          bolNotes: "Handle with care.",
          additionalFields: [],
        },
      },
    ],

    // Metadata fields (for PickupReviewDetailPage display)
    pickupNumber: pickupId,
    pickupStatus: statuses[index % statuses.length],
    pickupType: "Standard Pickup",
    pickupDateTime: "Thu, 7/10/2026 2:30 AM - 5:30 PM (PT)",
    quoteDate: "05/09/2025",
    accountExecutive: "Carlos Mendez",
    createdBy: "Carlos Mendez",
    createdOn: "05/09/2025",

    // Caller & Shipper summary (for PickupReviewDetailPage display)
    callerAccountLine: `${shipper.name} - #${shipper.code}`,
    callerAddressStreet: shipper.address,
    callerAddressCity: shipper.city,
    callerAddressState: shipper.state,
    callerAddressZip: shipper.zip,
    callerAddressCountry: "USA",

    shipperAccountLine: `${shipper.name} - #${shipper.code}`,
    shipperAddressStreet: shipper.address,
    shipperAddressCity: shipper.city,
    shipperAddressState: shipper.state,
    shipperAddressZip: shipper.zip,
    shipperAddressCountry: "USA",

    // Origin Service Center
    originServiceCentre: {
      label: "Origin Service Centre",
      name: "Los Angeles Distribution Center",
      code: "LAX-01",
      address: "1500 Harbor Blvd, Long Beach, CA 90012",
      phone: "+1(213)-555-5000",
      email: "losangeles@xyz.com",
    },

    // Pickup steps/timeline
    pickupSteps: [
      {
        label: "Pickup Created",
        date: "Fri, 03-13-2026",
      },
      {
        label: "Driver Assigned",
        date: "Est. Sat, 03-14-2026",
      },
      {
        label: "Driver On the Way",
        date: "Est. Sat, 03-14-2026",
      },
      {
        label: "Picked Up",
        date: "Est. Sat, 03-14-2026",
      },
    ],
  };
};

/**
 * Mock change log data for pickup review detail
 * Sorted in reverse chronological order (newest first)
 */
export const generateMockPickupChangeLog = (_pickupId: string) => [
  {
    id: "change-12",
    userId: "user-12",
    userName: "Jennifer",
    action: "updated",
    entityType: "Caller & Shipper Details",
    timestamp: "8/21/2024 9:15 AM",
    changes: [
      { field: "Caller", value: "Acme Distribution Inc. - #AC3456" },
      { field: "Caller Contact", value: "John Smith - +1(312)-555-0100" },
      { field: "Shipper", value: "Midwest Logistics LLC - #MW2089" },
      { field: "Shipper Contact", value: "Jane Doe - +1(480)-555-0200" },
    ],
  },
  {
    id: "change-11",
    userId: "user-11",
    userName: "Thomas",
    action: "updated",
    entityType: "Pickup Info",
    timestamp: "8/20/2024 4:20 PM",
    changes: [
      { field: "Pickup Type", value: "Standard Pickup" },
      { field: "Pickup Date", value: "Thu, 8/22/2024" },
      { field: "Ready Time", value: "8:00 AM" },
      { field: "Close Time", value: "5:00 PM" },
    ],
  },
  {
    id: "change-10",
    userId: "user-10",
    userName: "Lisa",
    action: "added",
    entityType: "Trace Numbers",
    timestamp: "8/20/2024 1:45 PM",
    changes: [
      { field: "Trace Number 1", value: "PO Number: 4578230041" },
      { field: "Precall", value: "Precall" },
    ],
  },
  {
    id: "change-9",
    userId: "user-9",
    userName: "Robert",
    action: "updated",
    entityType: "Daylight Protect Coverage",
    timestamp: "8/20/2024 11:00 AM",
    changes: [
      { field: "Purchase Coverage", value: "Yes" },
      { field: "Declare Amount", value: "$150,000" },
    ],
  },
  {
    id: "change-8",
    userId: "user-8",
    userName: "Emily",
    action: "added",
    entityType: "Driver Notes",
    timestamp: "8/19/2024 3:30 PM",
    changes: [
      {
        field: "Driver Notes",
        value: "Dock #4 - Call 30 minutes before arrival",
      },
      {
        field: "BOL Notes",
        value: "Freight shrink-wrapped and labeled by pallet",
      },
    ],
  },
  {
    id: "change-7",
    userId: "user-7",
    userName: "James",
    action: "updated",
    entityType: "Freight Items",
    timestamp: "8/19/2024 2:15 PM",
    changes: [
      { field: "Handling Unit 1", value: "1 Pallet" },
      { field: "Description", value: "Cartons of toys" },
      { field: "NMFC", value: "54545" },
      { field: "Class", value: "85" },
      { field: "Total Weight", value: "500 lbs" },
    ],
  },
  {
    id: "change-6",
    userId: "user-6",
    userName: "Sarah",
    action: "added",
    entityType: "Shipping Accessorial",
    timestamp: "8/19/2024 10:45 AM",
    changes: [
      { field: "Pickup Accessorial", value: "Inside Pickup Required" },
      { field: "Delivery Accessorial", value: "Lift Gate Service Required" },
      { field: "Both Accessorial", value: "Overlength 12 Ft" },
    ],
  },
  {
    id: "change-5",
    userId: "user-5",
    userName: "David",
    action: "updated",
    entityType: "Shipment #1",
    timestamp: "8/19/2024 9:20 AM",
    changes: [
      { field: "Consignee", value: "ABC Technologies - 200892" },
      { field: "Bill To", value: "Prepaid" },
      { field: "Total Weight", value: "1000 lbs" },
      { field: "Handling Units", value: "4 Pallets" },
    ],
  },
  {
    id: "change-4",
    userId: "user-4",
    userName: "Sophia",
    action: "processed",
    entityType: "Payment receipt",
    timestamp: "8/18/2024 4:45 PM",
    changes: [
      { field: "Service Level", value: "8/18/2024 4:45 PM" },
      { field: "Billing Terms", value: "Due on Receipt" },
      { field: "Billing Terms", value: "Due on Receipt" },
    ],
  },
  {
    id: "change-3",
    userId: "user-3",
    userName: "Michael",
    action: "added",
    entityType: "Pickup notes",
    timestamp: "8/17/2024 3:15 PM",
    changes: [
      { field: "Service Level", value: "8/17/2024 3:15 PM" },
      { field: "Billing Terms", value: "Net 30" },
      { field: "Billing Terms", value: "Net 30" },
    ],
  },
  {
    id: "change-2",
    userId: "user-2",
    userName: "Jessica",
    action: "updated",
    entityType: "Pickup entry Details",
    timestamp: "8/16/2024 1:30 PM",
    changes: [
      { field: "Service Level", value: "8/16/2024 1:30 PM" },
      { field: "Billing Terms", value: "Postpaid" },
      { field: "Billing Terms", value: "Postpaid" },
    ],
  },
  {
    id: "change-1",
    userId: "user-1",
    userName: "Michael",
    action: "updated",
    entityType: "Pickup entry Details",
    timestamp: "8/15/2024 12:00 AM",
    changes: [
      { field: "Service Level", value: "8/15/2024 12:00 AM" },
      { field: "Billing Terms", value: "Prepaid" },
      { field: "Billing Terms", value: "Prepaid" },
    ],
  },
];

/**
 * Same pickup id as pickup review detail tests; pickup entry summary/review hooks read these exports.
 */
const PICKUP_REVIEW_DETAIL_SYNC_PICKUP_ID = "PU-123456";

const pickupReviewDetailSyncRecord = generateMockPickupReviewDetailData(
  PICKUP_REVIEW_DETAIL_SYNC_PICKUP_ID
);

/** Default order for pickup entry review/summary — aligned with {@link generateMockPickupReviewDetailData}. */
export const mockOrderEntryData = pickupReviewDetailSyncRecord;

export const originServiceCentre =
  pickupReviewDetailSyncRecord.originServiceCentre;

export const mockPickupCallerShipperSummary = {
  callerAccountLine: pickupReviewDetailSyncRecord.callerAccountLine,
  callerAddressStreet: pickupReviewDetailSyncRecord.callerAddressStreet,
  callerAddressCity: pickupReviewDetailSyncRecord.callerAddressCity,
  callerAddressState: pickupReviewDetailSyncRecord.callerAddressState,
  callerAddressZip: pickupReviewDetailSyncRecord.callerAddressZip,
  callerAddressCountry: pickupReviewDetailSyncRecord.callerAddressCountry,
  callerContactName: pickupReviewDetailSyncRecord.callerContactName,
  callerContactPhone: pickupReviewDetailSyncRecord.callerContactPhone,
  shipperAccountLine: pickupReviewDetailSyncRecord.shipperAccountLine,
  shipperAddressStreet: pickupReviewDetailSyncRecord.shipperAddressStreet,
  shipperAddressCity: pickupReviewDetailSyncRecord.shipperAddressCity,
  shipperAddressState: pickupReviewDetailSyncRecord.shipperAddressState,
  shipperAddressZip: pickupReviewDetailSyncRecord.shipperAddressZip,
  shipperAddressCountry: pickupReviewDetailSyncRecord.shipperAddressCountry,
  shipperContactName: pickupReviewDetailSyncRecord.shipperContactName,
  shipperContactPhone: pickupReviewDetailSyncRecord.shipperContactPhone,
};

export const mockPickupMetadata = {
  pickupNumber: pickupReviewDetailSyncRecord.pickupNumber,
  pickupStatus: pickupReviewDetailSyncRecord.pickupStatus,
  quoteNumber: "QT-2024-001",
  quoteDate: pickupReviewDetailSyncRecord.quoteDate,
  pickupType: pickupReviewDetailSyncRecord.pickupType,
  pickupDateTime: pickupReviewDetailSyncRecord.pickupDateTime,
  accountExecutive: pickupReviewDetailSyncRecord.accountExecutive,
  createdBy: pickupReviewDetailSyncRecord.createdBy,
  pickupSteps: pickupReviewDetailSyncRecord.pickupSteps,
};

export const mockPickupEntrySummaryChangeLog = generateMockPickupChangeLog(
  PICKUP_REVIEW_DETAIL_SYNC_PICKUP_ID
);

/** Send Mail on pickup review detail — mock until API. */
export const PICKUP_REVIEW_SEND_MAIL_DEFAULT_RECIPIENTS = [
  "email@dylt.com",
  "customer.service@daylight.com",
] as const;

export const PICKUP_REVIEW_SEND_MAIL_EMAIL_SUGGESTIONS = [
  "email@dylt.com",
  "customer.service@daylight.com",
  "ops@dylt.com",
] as const;

export const PICKUP_REVIEW_SEND_MAIL_ISSUE_OPTIONS = [
  "Missing / Incomplete Information",
  "Additional Services Needed for Your Pickup",
  "Pallet/Weight Verification",
  "Possible Duplicate Pickup",
  "Follow-Up / No Response",
  "Driver Out of the Area",
  "Remote Point",
  "Others",
] as const;

/**
 * For these issues, Send Mail on Pickup Review clears recipient chips so addresses are entered manually.
 * Pass as `issueValuesRequiringManualRecipients` on the dialog from `PickupReviewDetailPage` only.
 */
export const PICKUP_REVIEW_SEND_MAIL_MANUAL_RECIPIENT_ISSUES = [
  "Others",
] as const;

const pickupReviewSendMailCustomerPlaceholder = "[Customer Name]";

const joinBody = (lines: string[]) => lines.join("\n");

/**
 * Subject + message body per issue for Send Mail dialog; `customerName` fills the greeting.
 * `"Others"` uses empty strings so the user fills subject and message manually.
 */
export const buildPickupReviewSendMailIssueTemplates = (
  customerName: string
): Record<string, { subject: string; body: string }> => {
  const name = customerName.trim() || pickupReviewSendMailCustomerPlaceholder;

  return {
    "Missing / Incomplete Information": {
      subject: "Additional Information Needed for Your Pickup Request",
      body: joinBody([
        `Hello ${name},`,
        "",
        "Thank you for your pickup request.",
        "",
        "We're currently reviewing the details and need a bit more information to move forward. Please provide the following:",
        "* [Missing Item #1]",
        "* [Missing Item #2]",
        "* [Missing Item #3]",
        "",
        "Once we receive this, we'll complete scheduling right away.",
        "",
        "If you have any questions, feel free to reply directly or give us a call. We're happy to help.",
        "",
        "Thank you,",
        "Daylight Transport",
      ]),
    },
    "Additional Services Needed for Your Pickup": {
      subject: "Additional Services Needed for Your Pickup",
      body: joinBody([
        `Hello ${name},`,
        "",
        "Thank you for your pickup request.",
        "",
        "While reviewing the shipment details, we identified that additional services may be required to complete this pickup successfully. Please confirm whether any of the following apply:",
        "• Liftgate (pickup and/or delivery)",
        "• Inside pickup or residential pickup",
        "• Appointment or limited access location",
        "• Other special handling: [describe]",
        "",
        "Please confirm which services are needed so we can update the quote and schedule the correct equipment.",
        "",
        "Additional charges may apply based on the services selected. We'll send an updated confirmation once everything is finalized.",
        "",
        "Thank you,",
      ]),
    },
    "Pallet/Weight Verification": {
      subject: "Please Verify Pallet Count and Weight",
      body: joinBody([
        `Hello ${name},`,
        "",
        "We're preparing to dispatch your pickup and want to confirm the pallet count and total weight before the driver arrives.",
        "",
        "Our records currently show:",
        "* Pallets / Handling Units: [Current PLTS]",
        "* Total Weight: [Current Weight] lbs",
        "* Dimensions (L x W x H): [Dimensions]",
        "",
        "Please reply to confirm these details are accurate, or share the corrected figures.",
        "",
        "Accurate weight and piece counts help us avoid re-weigh charges and ensure the right equipment is sent for pickup.",
        "",
        "Thank you,",
        "Customer Service Team",
      ]),
    },
    "Possible Duplicate Pickup": {
      subject: "Possible Duplicate Pickup Request – Please Confirm",
      body: joinBody([
        `Hello ${name},`,
        "",
        "Thank you for your pickup request.",
        "While reviewing this request, we noticed it may be a duplicate of a pickup already on file. For reference:",
        "",
        "Existing Pickup #: [P#]",
        "Origin: [Origin City, ST ZIP]",
        "Destination: [Destination City, ST ZIP]",
        "Scheduled Date: [Pickup Date]",
        "",
        "Could you confirm whether this is a new, separate shipment or the same one? If it's the same, no further action is needed on your side and we'll cancel the duplicate request.",
        "If this is a new shipment, please let us know so we can proceed with scheduling.",
        "",
        "Thank you,",
        "Customer Service Team",
      ]),
    },
    "Follow-Up / No Response": {
      subject: "Following Up on Your Pickup Request",
      body: joinBody([
        `Hello ${name},`,
        "",
        "We're following up on our earlier message regarding your pickup request. We haven't heard back yet and still need the information below to move forward:",
        "* [Outstanding Item #1]",
        "* [Outstanding Item #2]",
        "",
        "Please reply at your earliest convenience so we can finalize scheduling. If we don't hear back, the pickup may be delayed or cancelled.",
        "",
        "If this request is no longer needed, just let us know and we'll close it out on our end.",
        "",
        "Thank you,",
        "Customer Service Team",
      ]),
    },
    "Driver Out of the Area": {
      subject: "Pickup Scheduling Update – Driver Coverage",
      body: joinBody([
        `Hello ${name},`,
        "",
        "Thank you for your pickup request.",
        "The pickup location is currently outside our direct service area, so we're coordinating with a partner carrier to complete the pickup. This may slightly adjust the scheduled pickup window.",
        "",
        "We'll confirm the updated pickup date and time as soon as coverage is arranged.",
        "",
        "Thank you for your patience — we'll keep you updated every step of the way.",
        "",
        "Thank you,",
        "Customer Service Team",
      ]),
    },
    "Remote Point": {
      subject: "Remote Pickup Location – Additional Details",
      body: joinBody([
        `Hello ${name},`,
        "",
        "Thank you for your pickup request. The pickup address falls within a remote point / extended service area, which may affect transit time and pricing.",
        "To ensure accurate scheduling, please confirm:",
        "",
        "Exact pickup address and any gate or access codes",
        "Site contact name and phone number",
        "Available pickup hours and any site restrictions",
        "",
        "A remote point surcharge may apply. We'll send an updated quote once the details are confirmed so there are no surprises.",
        "",
        "Thank you,",
        "Customer Service Team",
      ]),
    },
    Others: {
      subject: "",
      body: "",
    },
  };
};
